import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { GetEachUserResponse } from '../types';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { authService } from '../services/auth.service';
import { API } from '../network/requests';
import ErrorPopup from '../components/ErrorPopup';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signingIn: boolean;
  signingOut: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: AuthError | null }>;
  signUp: (email: string, password: string) => Promise<{ error?: AuthError | null }>;
  signOut: () => Promise<void>;
  isAuthenticated: boolean;
  profile: GetEachUserResponse | null;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [profile, setProfile] = useState<GetEachUserResponse | null>(null);
  const hasLoadedProfileRef = useRef(false);
  const isLoadingProfileRef = useRef(false);
  const profileLoadPromiseRef = useRef<Promise<void> | null>(null);
  
  // Error popup state
  const [errorPopup, setErrorPopup] = useState({
    isOpen: false,
    title: '',
    message: ''
  });

  const showError = (title: string, message: string) => {
    setErrorPopup({
      isOpen: true,
      title,
      message
    });
  };

  const closeError = () => {
    setErrorPopup({
      isOpen: false,
      title: '',
      message: ''
    });
  };

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      const response = await authService.getCurrentSession();
      if (response.status === 'success' && response.data) {
        setSession(response.data?.session || null);
        setUser(response.data?.user ?? null);
        // Set API bearer token from Supabase access token
        const accessToken = response.data?.session?.access_token;
        if (accessToken) {
          API.setToken({ token: accessToken, expiresAt: null });
        }
        if (response.data?.session) {
          // Explicitly fetch current user information
          try {
            const userResp = await authService.getUser();
            if (userResp.status === 'success' && userResp.data?.user) {
              setUser(userResp.data.user);
            }
            // Auth is now ready for API usage
            API.markAuthReady();
          } catch {
            // ignore
          }
        }
        if (response.data?.session && !hasLoadedProfileRef.current && !isLoadingProfileRef.current) {
          await refreshProfile();
        }
      }
      setLoading(false);
    };

    getInitialSession();

    // Listen for auth changes
    const {
      data: { subscription },
    } = authService.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session) {
        // Update API bearer token when Supabase session changes
        if (session.access_token) {
          API.setToken({ token: session.access_token, expiresAt: null });
        }
        
        // Only verify user for specific events that require fresh data
        const shouldVerifyUser = event === 'USER_UPDATED' || event === 'TOKEN_REFRESHED';
        
        if (shouldVerifyUser) {
          try {
            (async () => {
              const userResp = await authService.getUser();
              if (userResp.status === 'success' && userResp.data?.user) {
                setUser(userResp.data.user);
              }
            })();
          } catch {
            // ignore
          }
        }
        
        // Auth is now ready for API usage (always call this)
        API.markAuthReady();
        
        if ((event === 'SIGNED_IN' || event === 'USER_UPDATED' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') && !hasLoadedProfileRef.current) {
          refreshProfile();
        }
      } else {
        // Clear API token on sign-out
        API.clearToken();
        API.resetAuthReady();
        setProfile(null);
        hasLoadedProfileRef.current = false;
        isLoadingProfileRef.current = false;
        profileLoadPromiseRef.current = null;
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const handleJwtSet = () => {
      // When token is set elsewhere (e.g., interceptor), try to refresh profile
      if (!hasLoadedProfileRef.current && !isLoadingProfileRef.current) {
        refreshProfile();
      }
    };
    const handleProfileRefreshed = (e: Event) => {
      try {
        const custom = e as CustomEvent<GetEachUserResponse>;
        if (custom.detail) {
          setProfile(custom.detail);
          hasLoadedProfileRef.current = true;
        }
      } catch {
        // ignore
      }
    };
    window.addEventListener('backend-jwt-set', handleJwtSet);
    window.addEventListener('profile-refreshed', handleProfileRefreshed as EventListener);
    return () => {
      window.removeEventListener('backend-jwt-set', handleJwtSet);
      window.removeEventListener('profile-refreshed', handleProfileRefreshed as EventListener);
    };
  }, []);

  const refreshProfile = async () => {
    
    // If already loading, return the existing promise
    if (isLoadingProfileRef.current && profileLoadPromiseRef.current) {
      return profileLoadPromiseRef.current;
    }
    
    // If already loaded, don't reload
    if (hasLoadedProfileRef.current) {
      return;
    }
    
    // Set loading state
    isLoadingProfileRef.current = true;
    
    // Create the profile loading promise
    const loadProfile = async () => {
      try {
        const res = await API.fetchLoggedInUser();
        const data = res?.data as GetEachUserResponse | undefined;
        if (data) {
          setProfile(data);
          hasLoadedProfileRef.current = true;
        }
      } catch (e) {
        console.error('Failed to refresh profile', e);
        setProfile(null);
        // showError('Profile Error', 'Failed to load user profile. Please try again.');
      } finally {
        isLoadingProfileRef.current = false;
        profileLoadPromiseRef.current = null;
      }
    };
    
    profileLoadPromiseRef.current = loadProfile();
    return profileLoadPromiseRef.current;
  };

  const signIn = async (email: string, password: string) => {
    try {
      setSigningIn(true);
      const response = await authService.signIn(email, password);
      
      if (response.status === 'error') {
        console.error('Sign in error:', response.error);
        return { error: response.error as AuthError };
      }

      // After successful Supabase sign-in, exchange for backend JWT before allowing navigation
      const sessionResponse = await authService.getCurrentSession();
      if (sessionResponse.status === 'error' || !sessionResponse.data?.session?.access_token) {
        const err = (sessionResponse.error as AuthError) ?? (new Error('No access token') as unknown as AuthError);
        console.error('Session fetch error:', err);
        return { error: err };
      }

      // Explicit backend login removed; token will be exchanged lazily via request interceptor

      // Profile refresh now no-ops until replacement; safe to continue

      return { error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      showError('Sign In Error', 'An unexpected error occurred during sign in. Please try again.');
      return { error: error as AuthError };
    } finally {
      setSigningIn(false);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      setLoading(true);
      const response = await authService.signUp(email, password);
      
      if (response.status === 'error') {
        console.error('Sign up error:', response.error);
        return { error: response.error as AuthError };
      }

      return { error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      showError('Sign Up Error', 'An unexpected error occurred during sign up. Please try again.');
      return { error: error as AuthError };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setSigningOut(true);
      const response = await authService.signOut();
      if (response.status === 'error') {
        console.error('Sign out error:', response.error);
      }
    } catch (error) {
      console.error('Sign out error:', error);
      showError('Sign Out Error', 'An unexpected error occurred during sign out. Please try again.');
    } finally {
      setSigningOut(false);
    }
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    signingIn,
    signingOut,
    signIn,
    signUp,
    signOut,
    isAuthenticated: !!user,
    profile,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <ErrorPopup
        isOpen={errorPopup.isOpen}
        onClose={closeError}
        title={errorPopup.title}
        message={errorPopup.message}
        type="error"
      />
    </AuthContext.Provider>
  );
};
